import { Timestamp } from "firebase/firestore";

import {
  LEGAL_EFFECTIVE_AT_MS,
  LEGAL_VERSION,
} from "../src/content/legalPolicies";

export const OWNER_LEGAL_ACCEPTANCE_FIELDS = Object.freeze([
  "version",
  "termsVersion",
  "privacyVersion",
  "refundVersion",
  "listingGuidelinesVersion",
  "acceptedAt",
  "updatedAt",
] as const);

export async function persistLegalAcceptance(
  save: () => Promise<void>
): Promise<boolean> {
  try {
    await save();
    return true;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[]
): boolean {
  const actual = Object.keys(value).sort();
  const required = [...expected].sort();
  return (
    actual.length === required.length &&
    actual.every((key, index) => key === required[index])
  );
}

export function hasCurrentOwnerLegalAcceptance(args: {
  ownerId: string;
  ownerDocument: unknown;
  nowMs: number;
}): boolean {
  if (!isRecord(args.ownerDocument)) return false;
  if (
    args.ownerDocument.uid !== args.ownerId ||
    args.ownerDocument.userId !== args.ownerId
  ) {
    return false;
  }

  const acceptance = args.ownerDocument.legalAccepted;
  if (
    !isRecord(acceptance) ||
    !hasExactKeys(acceptance, OWNER_LEGAL_ACCEPTANCE_FIELDS)
  ) {
    return false;
  }

  for (const field of [
    "version",
    "termsVersion",
    "privacyVersion",
    "refundVersion",
    "listingGuidelinesVersion",
  ] as const) {
    if (acceptance[field] !== LEGAL_VERSION) return false;
  }

  if (
    !(acceptance.acceptedAt instanceof Timestamp) ||
    !(acceptance.updatedAt instanceof Timestamp)
  ) {
    return false;
  }

  const acceptedAtMs = acceptance.acceptedAt.toMillis();
  const updatedAtMs = acceptance.updatedAt.toMillis();
  return (
    acceptedAtMs >= LEGAL_EFFECTIVE_AT_MS &&
    acceptedAtMs === updatedAtMs &&
    updatedAtMs <= args.nowMs
  );
}
